import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const tiers = [
  { label: '优秀示范', desc: '可推广的标杆课程', icon: 'ri-star-line', color: 'accent' },
  { label: '稳定达标', desc: '持续达标的日常课程', icon: 'ri-check-double-line', color: 'primary' },
  { label: '重点提升', desc: '需要重点支持的课程', icon: 'ri-arrow-up-circle-line', color: 'accent' },
];

const samples = [
  { code: '高职09', desc: '稳定中游样本', detail: '专家侧和学生侧位置接近，适合展示稳定达标而非极端样本', position: '人工两侧中部' },
  { code: '高职10', desc: '中游相邻样本', detail: '与高职09共同构成中游一致对照', position: '人工两侧中部' },
];

export default function P38QualityTiers({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-12">
        <div ref={titleRef} className="text-center mb-5 md:mb-7">
          <div className="flex items-center justify-center gap-2 mb-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100/60 text-primary-700 text-[11px] font-body font-semibold">
              <i className="ri-scan-line text-xs" /> 技术点八
            </span>
          </div>
          <h2 className="font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-bold text-foreground-950 transition-all duration-1000 delay-100">
            稳定达标课程与<span className="text-primary-600">三档质量分层</span>
          </h2>
        </div>

        {/* 三档阶梯 */}
        <div className="max-w-4xl mx-auto mb-6 transition-all duration-1000 delay-200">
          <div className="grid grid-cols-3 gap-4">
            {tiers.map((tier, i) => (
              <div key={tier.label} className={`p-5 rounded-2xl text-center transition-all duration-500 hover:scale-[1.03] ${tier.color === 'primary' ? 'glass-liquid border-primary-200/30' : 'glass-liquid-gold border-accent-200/30'}`} style={{ transitionDelay: `${200 + i * 100}ms` }}>
                <div className="relative z-10">
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${tier.color === 'primary' ? 'glass-blue border-primary-200/40' : 'glass-gold border-accent-200/40'}`}>
                    <i className={`${tier.icon} text-lg ${tier.color === 'primary' ? 'text-primary-600' : 'text-accent-600'}`} />
                  </div>
                  <h3 className={`font-heading text-base font-bold ${tier.color === 'primary' ? 'text-primary-700' : 'text-accent-700'}`}>{tier.label}</h3>
                  <p className="text-xs text-foreground-500 font-body mt-1">{tier.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 分档稳定性数据 */}
        <div className="max-w-3xl mx-auto mb-5 text-center transition-all duration-1000 delay-400">
          <div className="p-4 rounded-2xl glass-strong border-primary-200/30">
            <p className="text-xs text-foreground-500 font-body leading-relaxed">
              全量572门样本：三档分箱单调率约<span className="font-bold text-primary-700">96%</span>，四档约<span className="font-bold">76%</span>，五档约<span className="font-bold">38%</span>
            </p>
            <p className="text-[10px] text-foreground-400 mt-1">当前更稳妥的交付是三档质量诊断，过细分档会放大随机波动</p>
          </div>
        </div>

        {/* 中游样本 */}
        <div className="max-w-3xl mx-auto transition-all duration-1000 delay-600">
          <div className="grid grid-cols-2 gap-3">
            {samples.map((s) => (
              <div key={s.code} className="p-3 rounded-2xl glass border-white/50 text-center">
                <div className="flex items-center justify-center gap-2 mb-1.5">
                  <h3 className="font-heading text-sm font-bold text-foreground-800">{s.code}</h3>
                  <span className="text-[10px] text-foreground-400">{s.position}</span>
                </div>
                <p className="text-xs font-body font-semibold text-foreground-700">{s.desc}</p>
                <p className="text-[10px] text-foreground-400 mt-0.5">{s.detail}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}